import { useQuery } from '@tanstack/react-query'
import { MessageCircle } from 'lucide-react'
import apiClient from '@/api/client'

/**
 * Monthly WhatsApp send allowance meter. Meta bills per conversation, so the
 * plan carries a monthly cap enforced server-side at every enqueue — this
 * widget keeps the owner ahead of a campaign bouncing off the limit
 * (409 wa_allowance_insufficient) instead of surprised by it.
 */
export function WAAllowanceMeter() {
  const { data } = useQuery({
    queryKey: ['marketing', 'wa-allowance'],
    queryFn: () => apiClient.getWAAllowance(),
    staleTime: 60_000,
  })
  const a = data?.data
  if (!a) return null

  const pct = a.limit > 0 ? Math.min(100, Math.round((a.used / a.limit) * 100)) : 0
  const resetDate = a.resets_at
    ? new Date(a.resets_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : ''
  const tone =
    pct >= 90 ? 'bg-rose-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'

  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-border/80 px-3 py-2 text-xs">
      <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <div className="flex items-baseline gap-1.5">
          <span className="font-semibold tabular-nums">
            {a.used}/{a.limit}
          </span>
          <span className="text-muted-foreground">WhatsApp sends this month</span>
        </div>
        <div className="mt-1 h-1.5 w-36 overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {resetDate && (
        <span className="shrink-0 text-muted-foreground">resets {resetDate}</span>
      )}
    </div>
  )
}
